'use client';

/**
 * Canonical shared login page — the SINGLE login UI for every app.
 *
 * Per-app differences are ONLY branding (title, subtitle, logo, accent color)
 * passed as props. Behavior is identical everywhere:
 *   - Single "Sign In" action (email/username + password). No create-account.
 *   - Show/hide password toggle on every password field.
 *   - First-login "set new password" step (NEW_PASSWORD_REQUIRED).
 *
 * Engine-agnostic: it does NOT import any AuthProvider. The app passes the two
 * auth functions (login, completeNewPassword) so this same component works with
 * either the kit's SDK-based provider or an app's raw-HTTP provider. The app
 * also passes onAuthenticated() to navigate after success.
 */
import { useState, type CSSProperties, type FormEvent, type ReactNode } from 'react';

export interface LoginPageProps {
  /** Sign in. Resolve on success. Reject with an error whose `.code ===
   *  'NEW_PASSWORD_REQUIRED'` to trigger the set-new-password step. */
  login: (username: string, password: string) => Promise<void>;
  /** Complete the first-login password change. */
  completeNewPassword: (newPassword: string) => Promise<void>;
  /** Called after a successful sign-in (or password set). Do navigation here. */
  onAuthenticated: () => void;

  // ---- Branding (per-app look & feel; the only things that differ) ----
  appName?: string;
  subtitle?: string;
  /** Short logo text shown in the badge (e.g. "MF", "CTI"). */
  logoText?: string;
  /** Or a full logo node (img/svg). Overrides logoText when provided. */
  logo?: ReactNode;
  /** Accent color (buttons, logo badge, focus). CSS color string. */
  accentColor?: string;
  /** Login field label — some apps use email, some username-or-email. */
  usernameLabel?: string;
  usernamePlaceholder?: string;
  footerText?: string;
}

function EyeToggle({ shown, onClick }: { shown: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={shown ? 'Hide password' : 'Show password'}
      className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-gray-200"
    >
      {shown ? (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
        </svg>
      ) : (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
      )}
    </button>
  );
}

export default function LoginPage({
  login,
  completeNewPassword,
  onAuthenticated,
  appName = 'Sign In',
  subtitle = 'Sign in to continue',
  logoText,
  logo,
  accentColor = '#4f46e5',
  usernameLabel = 'Email',
  usernamePlaceholder = 'you@example.com',
  footerText = 'Access is restricted to authorized accounts.',
}: LoginPageProps) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [needsNewPassword, setNeedsNewPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const accentStyle: CSSProperties = { backgroundColor: accentColor };

  const handleLogin = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(username.trim(), password);
      onAuthenticated();
    } catch (err) {
      if (err && typeof err === 'object' && (err as { code?: string }).code === 'NEW_PASSWORD_REQUIRED') {
        setNeedsNewPassword(true);
      } else {
        setError(friendlyAuthError(err));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleNewPassword = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await completeNewPassword(newPassword);
      onAuthenticated();
    } catch (err) {
      setError(friendlyAuthError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-950">
      <div className="w-full max-w-md px-6 py-12 bg-gray-900 rounded-xl border border-gray-800 shadow-xl">
        {/* Logo / brand */}
        <div className="flex justify-center mb-6">
          {logo ? (
            logo
          ) : (
            <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={accentStyle}>
              <span className="text-white font-bold text-lg">{logoText || appName.slice(0, 2).toUpperCase()}</span>
            </div>
          )}
        </div>
        <h1 className="text-2xl font-bold text-white text-center mb-2">{appName}</h1>
        <p className="text-gray-400 text-center mb-8">{subtitle}</p>

        {error && (
          <div role="alert" className="mb-4 bg-red-500/10 border-2 border-red-500/40 rounded-lg px-4 py-3 text-sm text-red-200">
            {error}
          </div>
        )}

        {needsNewPassword ? (
          <form onSubmit={handleNewPassword} className="space-y-4">
            <p className="text-sm text-gray-300">This account needs a new password to continue.</p>
            <div>
              <label htmlFor="newPassword" className="text-xs text-gray-400 block mb-1">New Password</label>
              <div className="relative">
                <input
                  id="newPassword"
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-3 pr-11 py-2.5 text-sm text-gray-100 focus:outline-none focus:border-indigo-500"
                  placeholder="Enter a new password"
                />
                <EyeToggle shown={showNewPassword} onClick={() => setShowNewPassword((s) => !s)} />
              </div>
            </div>
            <button type="submit" disabled={submitting} style={accentStyle}
              className="w-full px-4 py-3 min-h-[44px] text-white font-semibold rounded-lg hover:opacity-90 disabled:opacity-60 transition-opacity">
              {submitting ? 'Saving...' : 'Set Password & Sign In'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label htmlFor="username" className="text-xs text-gray-400 block mb-1">{usernameLabel}</label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                autoComplete="username"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2.5 text-sm text-gray-100 focus:outline-none focus:border-indigo-500"
                placeholder={usernamePlaceholder}
              />
            </div>
            <div>
              <label htmlFor="password" className="text-xs text-gray-400 block mb-1">Password</label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-3 pr-11 py-2.5 text-sm text-gray-100 focus:outline-none focus:border-indigo-500"
                  placeholder="Your password"
                />
                <EyeToggle shown={showPassword} onClick={() => setShowPassword((s) => !s)} />
              </div>
            </div>
            <button type="submit" disabled={submitting} style={accentStyle}
              className="w-full px-4 py-3 min-h-[44px] text-white font-semibold rounded-lg hover:opacity-90 disabled:opacity-60 transition-opacity">
              {submitting ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
        )}

        <div className="mt-8 pt-6 border-t border-gray-800 text-center space-y-1">
          <p className="text-xs text-gray-500">{footerText}</p>
          <p className="text-xs text-gray-600">
            © {new Date().getFullYear()} AtalCloud. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
}

/** Maps auth errors to friendly, user-facing text. Never shows raw stack traces. */
export function friendlyAuthError(err: unknown): string {
  const raw = err instanceof Error ? err.message : String(err ?? '');
  if (/incorrect username or password|not authorized/i.test(raw)) return 'Incorrect email or password.';
  if (/user does not exist/i.test(raw)) return 'Incorrect email or password.';
  if (/user is disabled/i.test(raw)) return 'This account is disabled. Contact your administrator.';
  if (/password attempts exceeded|limit exceeded/i.test(raw)) return 'Too many attempts. Please wait a moment and try again.';
  if (/network|failed to fetch/i.test(raw)) return 'Cannot reach the server. Check your connection.';
  return 'Sign in failed. Please try again.';
}
